"use client";

import {
  easeInOut,
  motion,
  type MotionStyle,
  useScroll,
  useTransform,
} from "motion/react";
import { useRef } from "react";

import { EASE_OUT_EXPO, Reveal } from "@/components/motion/reveal";
import { useIntroDone } from "@/components/motion/stage";
import { VelocityMarquee } from "@/components/motion/velocity-marquee";
import { Image } from "@/components/shared/image";
import { PORTRAIT } from "@/constants/site";
import { usePrefersReducedMotion } from "@/hooks/use-prefers-reduced-motion";
import { cn } from "@/lib/utils";

import styles from "./hero.module.css";

// =============================================================================

type HeroProps = {
  marquee: string;
  left: string;
  right: string;
  photoAlt: string;
};

const fadeUp = (delay: number) => ({
  hidden: { opacity: 0, y: 28, filter: "blur(8px)" },
  visible: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: 1, delay, ease: EASE_OUT_EXPO },
  },
});

const fadeIn = (delay: number) => ({
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 1.2, delay } },
});

const MARQUEE_TEXT =
  "text-[4.5rem] leading-none font-extrabold tracking-[-0.03em] text-hero-marquee uppercase hero-wide:text-[10.1cqw]";

/**
 * Home hero. On load the frame scales in, then the texts appear. While
 * scrolling (sticky), the texts fade, the frame collapses into a line that is
 * un-drawn towards its center, and the portrait shrinks (small screens: the
 * texts follow right below).
 */
export function Hero({ marquee, left, right, photoAlt }: HeroProps) {
  const sectionRef = useRef<HTMLElement>(null);
  const shouldReduceMotion = usePrefersReducedMotion();
  const introDone = useIntroDone();
  const state = introDone ? "visible" : "hidden";

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end end"],
  });
  // Function transform: no native scroll acceleration, whose timeline fills
  // the ends of the ranges with the base style (the faded portrait came back
  // at the end of the hero)
  const progress = useTransform(scrollYProgress, (value) => value);

  // 1. Texts and marquees out
  const textOpacity = useTransform(progress, [0, 0.14], [1, 0]);
  const textY = useTransform(progress, [0, 0.14], [0, -48]);
  const marqueeOpacity = useTransform(progress, [0.04, 0.3], [1, 0]);
  // 2. The frame collapses towards its bottom edge while it is un-drawn from
  //    both sides: nothing is left once the collapse is over
  const collapse = useTransform(progress, [0.08, 0.44], [0, 1]);
  const undraw = useTransform(progress, [0.2, 0.46], [0, 1], {
    ease: easeInOut,
  });
  const clipPath = useTransform([collapse, undraw], ([p, s]: number[]) => {
    const side = `${(s * 50).toFixed(3)}%`;
    return `inset(calc(${(p * 100).toFixed(3)}% - ${p.toFixed(3)}px) ${side} 0px ${side} round calc(${(1 - p).toFixed(3)} * 4vw))`;
  });
  // 3. The portrait shrinks and stays above what follows
  const photoScale = useTransform(progress, [0.22, 0.78], [1, 0.55]);
  // Wide screens: the bottom of the bust fades while it shrinks (no visible
  // straight cut, the screen edge no longer hides it)
  const photoMask = useTransform(progress, [0.2, 0.5], [100, 55], {
    clamp: true,
  });
  const photoMaskImage = useTransform(
    photoMask,
    (stop) =>
      `linear-gradient(to bottom, #000 ${stop}%, transparent ${Math.min(100, stop + 40)}%)`
  );

  const scrollStyle = <T,>(style: T) =>
    shouldReduceMotion ? undefined : style;

  return (
    <>
      <section
        ref={sectionRef}
        aria-label={photoAlt}
        className="relative h-[150svh] hero-wide:h-[230svh]"
      >
        <div className="sticky top-0 h-svh overflow-hidden">
          <div className={cn(styles.stage, "absolute inset-0")}>
            <div className={styles.frameBox}>
              {/* Frame */}
              <motion.div
                className="@container absolute inset-x-(--hero-gutter) top-[13%] h-[72%] hero-wide:inset-0 hero-wide:h-full"
                style={scrollStyle({ clipPath })}
              >
                <motion.div
                  variants={{
                    hidden: { opacity: 0, scale: 0.86 },
                    visible: {
                      opacity: 1,
                      scale: 1,
                      transition: { duration: 1.2, ease: EASE_OUT_EXPO },
                    },
                  }}
                  initial="hidden"
                  animate={state}
                  className="size-full rounded-[2.25rem] border border-hero-border bg-hero-gap p-2 hero-wide:rounded-[6.6cqw] hero-wide:p-[0.9cqw]"
                >
                  <div className="relative size-full overflow-hidden rounded-[1.75rem] bg-gradient-to-b from-hero-panel-from to-hero-panel-to hero-wide:rounded-[5.75cqw]">
                    <motion.div
                      className="pointer-events-none absolute inset-x-0 top-[6%] hero-wide:top-[4.2%]"
                      style={scrollStyle({ opacity: marqueeOpacity })}
                      aria-hidden
                    >
                      <motion.div
                        className={MARQUEE_TEXT}
                        variants={fadeIn(0.35)}
                        initial="hidden"
                        animate={state}
                      >
                        <VelocityMarquee baseVelocity={-1.4}>
                          <span className="pr-[0.3em]">{marquee} –</span>
                        </VelocityMarquee>
                      </motion.div>
                    </motion.div>
                    <motion.div
                      className="pointer-events-none absolute inset-x-0 bottom-[22%] hero-wide:bottom-[5.1%]"
                      style={scrollStyle({ opacity: marqueeOpacity })}
                      aria-hidden
                    >
                      <motion.div
                        className={MARQUEE_TEXT}
                        variants={fadeIn(0.45)}
                        initial="hidden"
                        animate={state}
                      >
                        <VelocityMarquee baseVelocity={1.4}>
                          <span className="pr-[0.3em]">{marquee} –</span>
                        </VelocityMarquee>
                      </motion.div>
                    </motion.div>

                    {/* Wide screens: texts inside the frame */}
                    <motion.div
                      className="absolute inset-x-[2.7cqw] top-[34.8%] hidden justify-between gap-[3cqw] hero-wide:flex"
                      style={scrollStyle({ opacity: textOpacity, y: textY })}
                    >
                      <motion.p
                        variants={fadeUp(0.75)}
                        initial="hidden"
                        animate={state}
                        className="w-[29cqw] text-[1.8cqw] leading-[1.25]"
                      >
                        {left}
                      </motion.p>
                      <motion.p
                        variants={fadeUp(0.9)}
                        initial="hidden"
                        animate={state}
                        className="w-[27.5cqw] text-right text-[1.8cqw] leading-[1.25]"
                      >
                        {right}
                      </motion.p>
                    </motion.div>
                  </div>
                </motion.div>
              </motion.div>

              {/* Portrait, in front of the frame: the top of the head sits
                  right below the top marquee, the bust leaves the frame */}
              <motion.div
                className="pointer-events-none absolute top-[25%] left-1/2 aspect-[995/1279] w-[min(94%,27rem,60svh)] -translate-x-1/2 hero-wide:top-[27.2%] hero-wide:w-[41%] hero-wide:mask-(--photo-mask)"
                style={scrollStyle({
                  scale: photoScale,
                  "--photo-mask": photoMaskImage,
                  transformOrigin: "50% 75%",
                } as MotionStyle)}
              >
                <motion.div
                  // Small screens: the bust fades at the bottom and on the
                  // sides, where the photo cuts the arms
                  className="relative size-full mask-[linear-gradient(to_bottom,#000_68%,transparent_97%),linear-gradient(to_right,transparent,#000_14%,#000_86%,transparent)] mask-intersect hero-wide:mask-none"
                  variants={{
                    hidden: { opacity: 0, y: 70 },
                    visible: {
                      opacity: 1,
                      y: 0,
                      transition: {
                        duration: 1.3,
                        delay: 0.45,
                        ease: EASE_OUT_EXPO,
                      },
                    },
                  }}
                  initial="hidden"
                  animate={state}
                >
                  <Image
                    src={PORTRAIT.src}
                    alt={photoAlt}
                    fill
                    loading="eager"
                    fetchPriority="high"
                    sizes="(min-width: 768px) 40vw, 90vw"
                    className="object-contain object-top"
                  />
                </motion.div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Small and portrait screens: the texts follow the hero, right below
          the shrunken portrait, in a frame like the one that just collapsed */}
      <div
        className={cn(
          styles.texts,
          "relative container-page pt-4 pb-6 hero-wide:hidden"
        )}
      >
        <Reveal
          y={24}
          className="mx-auto max-w-2xl rounded-[1.75rem] border border-hero-border bg-hero-gap p-1.5"
        >
          <div className="space-y-5 rounded-[1.375rem] bg-gradient-to-b from-hero-panel-from to-hero-panel-to px-5 py-6 text-[1.0625rem] leading-relaxed xs:px-6 sm:space-y-6 sm:px-9 sm:py-9 sm:text-lg">
            <p>{left}</p>
            <div aria-hidden className="h-px bg-foreground/10" />
            <p>{right}</p>
          </div>
        </Reveal>
      </div>
    </>
  );
}

// =============================================================================
